import { GetMcaEstimatedScheduleResponse } from '@iwoca/lapi-client/edge';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';

import TipBox from './components/TipBox';
import styles from './EstimatedRepayments.module.css';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';
import { VariableRate } from '../../../ui/rate/Rate';

const EstimatedRepayments = ({
    schedule,
    className,
    loading
}: {
    schedule: GetMcaEstimatedScheduleResponse;
    className: string;
    loading: boolean;
}) => {
    return (
        <div className={className}>
            <div className={styles.estimatedRepaymentsTitle}>
                <FormattedMessage
                    id="_.rbl_dashboard.approved_estimated_repayments_title"
                    defaultMessage="Based on your revenue now, here’s what your monthly repayments could look like."
                />
            </div>

            <div className={styles.estimatedRepaymentsContent}>
                <div
                    data-testid="monthly-revenue"
                    className={styles.summaryRow}
                >
                    <div className={styles.estimatedRepaymentsColumn}>
                        <div>
                            <FormattedMessage
                                id="_.rbl_dashboard.approved_estimated_repayments_monthly_revenue"
                                defaultMessage="Your monthly eligible sales revenue:"
                            />
                        </div>
                        <div className={styles.averageRevnueCopy}>
                            <FormattedMessage
                                id="_.rbl_dashboard.approved_estimated_repayments_monthly_revenue_explanation"
                                defaultMessage="(the average of your last three months’ eligible sales)"
                            />
                        </div>
                    </div>
                    <MoneyNoDecimals
                        className={cn(styles.summaryAmount, {
                            [styles.loading]: loading
                        })}
                        value={
                            schedule.data.estimated_schedule
                                .revenue_amount_per_month
                        }
                    />
                </div>

                <div
                    data-testid="estimated-repayment-amount"
                    className={styles.summaryRow}
                >
                    <span className={styles.estimatedRepaymentsLabel}>
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_estimated_repayments_amount"
                            defaultMessage="Estimated monthly repayments ({percentage}):"
                            values={{
                                percentage: (
                                    <span
                                        className={cn({
                                            [styles.loading]: loading
                                        })}
                                    >
                                        <VariableRate
                                            minimumFractionDigits={0}
                                            maximumFractionDigits={2}
                                            value={
                                                schedule.data.summary.sweep
                                                    .selected_sweep_fraction
                                            }
                                        />
                                    </span>
                                )
                            }}
                        />
                    </span>

                    <MoneyNoDecimals
                        className={cn(styles.summaryAmount, {
                            [styles.loading]: loading
                        })}
                        value={
                            schedule.data.estimated_schedule
                                .estimated_monthly_repayment_amount
                        }
                    />
                </div>

                <div
                    data-testid="months-to-repay"
                    className={styles.summaryRow}
                >
                    <FormattedMessage
                        id="_.rbl_dashboard.approved_estimated_time_to_repay"
                        defaultMessage="Time to repay:"
                    />{' '}
                    <span
                        className={cn(styles.summaryAmount, {
                            [styles.loading]: loading
                        })}
                    >
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_estimated_repayment_in_months"
                            defaultMessage="{months} months"
                            values={{
                                months: schedule.data.estimated_schedule
                                    .estimated_time_to_repay_in_months
                            }}
                        />
                    </span>
                </div>
            </div>

            <TipBox
                tip={
                    <FormattedMessage
                        id="_.rbl_dashboard.approved_estimated_tip_info"
                        defaultMessage="This is based on your revenue over the past 3 months - your performance will affect how long you repay for."
                    />
                }
            />
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default EstimatedRepayments;
